import { Auth0ProviderWithNavigate } from "@/auth/auth0-provider-with-navigate";
import { FunnelAuthenticator } from "@/components/FunnelAuthenticator/FunnelAuthenticator";
import { AuthGuard } from "@/components/GuardComponents/AuthGuard";
import { CookieGuard } from "@/components/GuardComponents/CookieGuard";
import { OnboardingGuard } from "@/components/GuardComponents/OnboardingGuard";
import { OrgGuard } from "@/components/GuardComponents/OrgGuard";
import { TermsGuard } from "@/components/GuardComponents/TermsGuard";
import { VerifiedEmailGuard } from "@/components/GuardComponents/VerifiedEmailGuard";
import { Loading } from "@/components/Loading/Loading";
import { UploadProgressDrawer } from "@/components/ProgressDrawer/UploadProgressDrawer";
import { Snackbar } from "@/components/Snackbar/Snackbar";
import { useIndexedDBInvalidation } from "@/hooks/indexeddb";
import { getStorageValue, LOCAL_STORAGE_KEYS, setStorageValue } from "@/hooks/localstorage";
import { i18n } from "@/i18n";
import { LoadLinks } from "@/links";
import { HomePage } from "@/pages/Home/page";
import { Integrate } from "@/pages/Integrate/Integrate";
import { Manage } from "@/pages/Manage/Manage";
import { NotFound } from "@/pages/NotFound/NotFound";
import { AwaitingEmailVerification } from "@/pages/Onboarding/AwaitingEmailVerification";
import { Onboarding } from "@/pages/Onboarding/Onboarding";
import { sec } from "@/store/security";
import { OrgGuardRequiredStatus } from "@/typed/OrgGuardRequiredStatus";
import { Beacon } from "@/utils/beacon";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { FC, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";

const LandingPageRedirect = ({ landingPath, newUserLandingPath }: { landingPath: string; newUserLandingPath?: string }) => {
  const isNewUser = getStorageValue(LOCAL_STORAGE_KEYS.isNewUser, false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isNewUser) {
      if (newUserLandingPath) {
        setStorageValue(LOCAL_STORAGE_KEYS.isNewUser, false);
        navigate(newUserLandingPath);
        return;
      }
    }
    navigate(landingPath);
  });
  return <>{null}</>;
};

function InnerApp() {
  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
  const { PrimaryLinks, FooterLinks } = LoadLinks();

  const credits = () => {
    window.location.href = "/credits/";
    return <></>;
  };

  const { getAccessTokenSilently } = useAuth0();
  sec.setAccessTokenSilently(getAccessTokenSilently);

  const APIAuthorizer: FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    sec.setAccessTokenSilently(getAccessTokenSilently);

    return null;
  };

  useEffect(() => {
    Beacon("init", import.meta.env.VITE_BEACON_PUBLIC_SECRET);
  }, []);

  useIndexedDBInvalidation();

  return (
    <HelmetProvider>
      <Helmet titleTemplate="%s - Genira"></Helmet>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Auth0ProviderWithNavigate>
          <I18nextProvider i18n={i18n}>
            <SentryRoutes>
              <Route
                path="/"
                element={
                  <AuthGuard
                    component={
                      <VerifiedEmailGuard>
                        <TermsGuard>
                          <FunnelAuthenticator />
                          <LandingPageRedirect landingPath="/cases" newUserLandingPath="/manage" />
                        </TermsGuard>
                      </VerifiedEmailGuard>
                    }
                  />
                }
              />
              <Route
                path="/cases/:id?"
                element={
                  <AuthGuard
                    component={
                      <VerifiedEmailGuard>
                        <TermsGuard>
                          <CookieGuard>
                            <HomePage />
                          </CookieGuard>
                        </TermsGuard>
                      </VerifiedEmailGuard>
                    }
                  />
                }
              />
              <Route
                path="/manage/:id?"
                element={
                  <AuthGuard
                    component={
                      <VerifiedEmailGuard>
                        <TermsGuard>
                          <CookieGuard>
                            <Manage primaryLinks={PrimaryLinks} footerLinks={FooterLinks}></Manage>
                          </CookieGuard>
                        </TermsGuard>
                      </VerifiedEmailGuard>
                    }
                  />
                }
              />
              <Route
                path="/integrate"
                element={
                  <VerifiedEmailGuard>
                    <TermsGuard>
                      <Integrate />
                    </TermsGuard>
                  </VerifiedEmailGuard>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <OnboardingGuard>
                    <Onboarding />
                  </OnboardingGuard>
                }
              />
              <Route
                path="/join"
                element={
                  <AuthGuard
                    component={
                      <OrgGuard requiredOrgStatus={OrgGuardRequiredStatus.NO_ORG} redirectRoute="/cases">
                        <Loading />
                      </OrgGuard>
                    }
                  ></AuthGuard>
                }
              />
              <Route path="/verify-email" element={<AuthGuard component={<AwaitingEmailVerification />} />} />
              <Route path="*" element={<AuthGuard component={<NotFound primaryLinks={PrimaryLinks} footerLinks={FooterLinks} />} />} />
              <Route path="/credits" element={<AuthGuard component={credits} />} />
            </SentryRoutes>
            <UploadProgressDrawer />
            <APIAuthorizer />
          </I18nextProvider>
        </Auth0ProviderWithNavigate>
      </BrowserRouter>
      <Snackbar />
    </HelmetProvider>
  );
}

export const App = Sentry.withProfiler(InnerApp);
